import Service from '@ember/service';
import getConfig from '../lib/get-config.js';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — vendor boot glue for third-party window-global SDKs
 *              (Intercom, Microsoft Clarity, LiveChatWidget)
 *   extends:   Service (ember, base class)
 *   template:  none
 *   key:       initIntercom(me, isSmallDevice), initClarity, configureLiveChat(me,
 *              freshProperties)
 *   uses:      lib/get-config (getConfig, for Clarity's app id), global
 *              window.Intercom/window.LiveChatWidget/document (script injection)
 *   siblings:  controllers/application.js (calls all three during boot/session
 *              setup)
 *   tests:     third-party-boot-test.js
 *   gotchas:   configureLiveChat no-ops silently if window.LiveChatWidget hasn't
 *              loaded yet (checks widget.call/widget.on are functions) — no
 *              retry/queueing, a call before SDK load is just dropped
 *
 * Servizio ThirdPartyBoot
 * Racchiude la vendor glue di boot degli SDK di terze parti (window-global) —
 * Intercom, Microsoft Clarity e LiveChatWidget.
 * Estratto dal god-controller application (maintainability 4.2), comportamento invariato.
 */
class ThirdPartyBootService extends Service {
  /**
   * Inizializza Intercom widget
   * @param {Object} me - Utente corrente
   * @param {boolean} isSmallDevice - Deriva dallo stato del controller (hide_default_launcher)
   */
  /* istanbul ignore next -- external Intercom SDK DOM-injection glue (§11.4) */
  initIntercom(me, isSmallDevice) {
    (function () {
      const w = window;
      const ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
      } else {
        const d = document;
        const i = function () {
          i.c(arguments);
        };
        i.q = [];
        i.c = function (args) {
          i.q.push(args);
        };
        w.Intercom = i;
        const l = function () {
          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://widget.intercom.io/widget/j752u2c7';
          const x = d.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    })();
    window.Intercom('boot', {
      api_base: 'https://api-iam.intercom.io',
      app_id: 'j752u2c7',
      user_id: `${me.id}-${me.organization?.organization}`,
      intercom_user_jwt: me.intercom_user_jwt,
      name: me.label,
      email: me.mail,
      created_at: me.created_at,
      company: {
        id: me.organization?.organization,
        name: me.organization?.name,
        plan: me.organization?.subscription?.plan?.name,
        url: `https://${me.organization?.organization}.beebeeboard.com`,
        trial: me.organization?.subscription?.trial,
        expire_date: me.organization?.subscription?.expire_date
      },
      hide_default_launcher: isSmallDevice
    });
  }

  /**
   * Inizializza Microsoft Clarity
   */
  /* istanbul ignore next -- external Microsoft Clarity SDK script-injection glue (§11.4) */
  initClarity() {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
      t = l.createElement(r);
      t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', getConfig(this).APP.clarity);
  }

  /**
   * Configura LiveChatWidget con i dati utente
   * @param {Object} me - Utente corrente
   * @param {Object} freshProperties - Proprietà sessione derivate dal controller (setFreshProperties)
   */
  /* istanbul ignore next -- external LiveChatWidget SDK session/visibility glue (§11.4) */
  configureLiveChat(me, freshProperties) {
    const widget = window.LiveChatWidget;
    if (typeof widget?.call !== 'function' || typeof widget?.on !== 'function') {
      return;
    }
    if (me.label) {
      widget.call('set_customer_name', me.label);
    }
    if (me.mail) {
      widget.call('set_customer_email', me.mail);
    }
    widget.call('update_session_variables', freshProperties);
    widget.call('hide');
    widget.on('visibility_changed', function (data) {
      if (data.visibility === 'minimized' || data.visibility === 'hide') {
        widget.call('hide');
      }
    });
  }
}

export { ThirdPartyBootService as default };
//# sourceMappingURL=third-party-boot.js.map
